import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { RotateCcw, Trash2, Search, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PassiveQuestions() {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { status: "passive", limit: 100 };
      if (search) params.search = search;
      const res = await api.get("/questions", { params });
      setQuestions(res.data.questions);
      setTotal(res.data.total);
    } catch { toast.error("Yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [search]);

  const handleRestore = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/questions/${id}/restore`); toast.success("Soru aktif edildi"); fetch(); }
    catch { toast.error("İşlem başarısız"); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Kalıcı olarak silmek istediğinizden emin misiniz?")) return;
    try { await api.delete(`/questions/${id}`); toast.success("Soru silindi"); fetch(); }
    catch { toast.error("Silme başarısız"); }
  };

  return (
    <div className="p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pasif Sorular</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} pasif soru</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="relative max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Soru ara..." value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="passive-search-input" className="pl-8 h-8 text-xs border-slate-200" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="passive-questions-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-28">Kod</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Soru</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-32">Ders</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-32">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i} className="border-b border-slate-50">
                  {[...Array(4)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                </tr>
              ))
            ) : questions.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center">
                <EyeOff size={28} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Pasif soru bulunamadı</p>
              </td></tr>
            ) : questions.map((q, i) => (
              <tr key={q.id}
                className={`hover:bg-slate-50 transition-colors cursor-pointer ${i < questions.length - 1 ? "border-b border-slate-100" : ""}`}
                onClick={() => navigate(`/questions/${q.id}`)}
                data-testid={`passive-row-${q.question_code}`}>
                <td className="px-4 py-3"><span className="font-mono text-xs text-slate-500">{q.question_code}</span></td>
                <td className="px-4 py-3 text-slate-600 text-xs max-w-sm truncate">{q.question_text}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{q.lesson_name || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => handleRestore(q.id, e)} data-testid={`restore-btn-${q.question_code}`}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 border border-green-200 hover:bg-green-50 rounded transition-colors">
                      <RotateCcw size={11} /> Aktife Al
                    </button>
                    <button onClick={(e) => handleDelete(q.id, e)} data-testid={`delete-btn-${q.question_code}`}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Kalıcı Sil">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
